import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, FormHelperText,
  Grid, InputLabel, MenuItem, Paper, Radio, RadioGroup, Select, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SpeedIcon from '@mui/icons-material/Speed';
import MapIcon from '@mui/icons-material/Map';
import { useSnackbar } from 'notistack';
import { api } from '../../services/api';
import { SCHEDULE_STEP_OPTIONS, defaultStartLocal } from '../../utils/optimizeScheduleUtils';
import IperfTestTargetFields from './IperfTestTargetFields';
import MapSelectDialog from './MapSelectDialog';
import { capsOfSerials } from '../../utils/mapSelectUtils';
import { iperfTestFormFromRequest, iperfTestFormProblem, iperfTestFormToRequest } from '../../utils/iperfScheduleUtils';
import ScheduleWhenFields, { scheduleWhenProblems, useSiteTimezones, useTimezoneOptions } from './ScheduleWhenFields';
import {
  SCHEDULE_PARAM_GROUPS,
  SCHEDULE_SCOPE_OPTIONS,
  changedParamCounts,
  defaultScheduleSettings,
  optimizeRequestToSettings,
  settingsToOptimizeRequest,
} from '../../utils/optimizeScheduleSettings';
import { listCapDevices } from '../../utils/optimizeScopeUtils';
import { UNII_CHANNEL_GROUPS, defaultOptimizeChannelGroupSummary } from '../../utils/uniiChannelUtils';

const ALL_STEPS = SCHEDULE_STEP_OPTIONS.map((o) => o.value);

/** One optimize parameter, rendered from the schema. */
function ParamField({ param, value, onChange }) {
  if (param.type === 'bool') {
    return (
      <Tooltip title={param.help || ''} placement="top-start">
        <FormControlLabel
          control={<Switch size="small" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />}
          label={<Typography variant="body2">{param.label}</Typography>}
        />
      </Tooltip>
    );
  }
  if (param.type === 'select') {
    return (
      <FormControl size="small" fullWidth>
        <InputLabel>{param.label}</InputLabel>
        <Select label={param.label} value={value} onChange={(e) => onChange(e.target.value)}>
          {param.options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }
  return (
    <Tooltip title={param.help || ''} placement="top-start">
      <TextField
        size="small"
        fullWidth
        type="number"
        label={param.label}
        value={value}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        inputProps={{ min: param.min, max: param.max, step: param.step }}
      />
    </Tooltip>
  );
}

/**
 * One stage of a scheduled run, as a card: numbered header (number shown
 * only for stages that run), title, one-line explanation, optional header
 * action (e.g. an on/off switch), and the stage's own fields inside, so it
 * is clear which settings belong to which stage.
 */
function StepCard({ step, color, icon, title, subtitle, action, active, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: active ? `${color}.main` : 'divider',
        borderLeftWidth: 4,
        borderLeftColor: active ? `${color}.main` : 'divider',
        opacity: active ? 1 : 0.85,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ p: 1.5, pb: children ? 1 : 1.5 }}>
        <Chip
          size="small"
          color={active ? color : 'default'}
          icon={icon}
          label={step ? `Step ${step}` : 'Optional'}
          sx={{ mt: 0.25, fontWeight: 600 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.4 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">{subtitle}</Typography>
        </Box>
        {action}
      </Stack>
      {children && <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>{children}</Box>}
    </Paper>
  );
}

function deviceLabel(device) {
  const name = device?.name || device?.display_name;
  return name ? `${name} (${device.serial_number})` : device?.serial_number;
}

/**
 * Create or edit a scheduled "optimize & apply". Each schedule carries its
 * own optimize configuration (scope and parameters), edited here; the
 * Optimize tab's current scope and parameters can be copied in as a
 * starting point. The copy is a snapshot: later changes in either place
 * never affect the other.
 * Times are in the site's timezone, taken from the site record.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {object|null} props.schedule existing schedule to edit, or null to create
 * @param {Array} [props.devices] site devices, for the CAP scope picker
 * @param {() => (object|null)} [props.copyFromOptimize] Optimize tab settings getter
 * @param {() => void} props.onClose
 * @param {(schedule:object) => void} props.onSaved
 */
export default function OptimizeScheduleDialog({ open, schedule, devices, copyFromOptimize, onClose, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const editing = Boolean(schedule);
  // The form is only built once the site lookup has finished (ok or not),
  // so it starts with the site's timezone.
  const { timezones, loaded: tzLoaded, error: tzError } = useSiteTimezones(open);
  const [customTrees, setCustomTrees] = useState([]);
  // A tree is only reported missing once the list has loaded.
  const [treesLoaded, setTreesLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [scopeMapOpen, setScopeMapOpen] = useState(false);

  const caps = useMemo(() => listCapDevices(devices || []), [devices]);
  const capBySerial = useMemo(() => new Map(caps.map((c) => [c.serial_number, c])), [caps]);
  const deviceBySerial = useMemo(() => new Map((devices || []).map((d) => [d.serial_number, d])), [devices]);

  // The custom trees for the scope picker.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setTreesLoaded(false);
    api.getCustomTrees()
      .then((data) => {
        if (cancelled) return;
        setCustomTrees(Array.isArray(data?.trees) ? data.trees : []);
        setTreesLoaded(true);
      })
      .catch(() => { if (!cancelled) setCustomTrees([]); });
    return () => { cancelled = true; };
  }, [open]);

  // Initialise the form when opened (and once the site timezone is known).
  useEffect(() => {
    if (!open) {
      setForm(null);
      return;
    }
    if (form) return;
    if (editing) {
      setForm({
        name: schedule.name || '',
        enabled: schedule.enabled !== false,
        timezone: schedule.timezone || '',
        startLocal: schedule.start_local || '',
        repeat: schedule.repeat || 'weekly',
        steps: schedule.steps || [],
        persistTree: Boolean(schedule.persist_tree),
        preIperf: Boolean(schedule.pre_iperf),
        iperfTest: iperfTestFormFromRequest(schedule.iperf_request, caps),
        tolerantReparent: schedule.tolerant_reparent !== false,
        settings: optimizeRequestToSettings(schedule.optimize_request || {}),
      });
    } else if (tzLoaded) {
      const tz = timezones[0] || '';
      setForm({
        name: 'Weekly optimize & apply',
        enabled: true,
        timezone: tz,
        startLocal: defaultStartLocal(tz),
        repeat: 'weekly',
        steps: ALL_STEPS,
        persistTree: false,
        preIperf: false,
        iperfTest: iperfTestFormFromRequest(null, caps),
        tolerantReparent: true,
        settings: defaultScheduleSettings(),
      });
    }
  }, [open, form, editing, schedule, timezones, tzLoaded, caps]);

  // Offer the stored timezone even if the site record changed since.
  const tzOptions = useTimezoneOptions(timezones, form?.timezone);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setSetting = (key, value) => setForm((f) => ({ ...f, settings: { ...f.settings, [key]: value } }));
  const toggleStep = (step) => setForm((f) => ({
    ...f,
    steps: f.steps.includes(step) ? f.steps.filter((s) => s !== step) : [...f.steps, step],
  }));

  const resetParams = () => setForm((f) => ({
    ...f,
    settings: {
      ...defaultScheduleSettings(),
      scopeMode: f.settings.scopeMode,
      capSerials: f.settings.capSerials,
      customTreeIds: f.settings.customTreeIds,
      focusSerials: f.settings.focusSerials,
    },
  }));

  const copyOptimizeTab = () => {
    const fromOptimize = typeof copyFromOptimize === 'function' ? copyFromOptimize() : null;
    if (!fromOptimize) {
      enqueueSnackbar('The Optimize tab has not been opened yet, so there is nothing to copy.', { variant: 'info' });
      return;
    }
    if (fromOptimize.error) {
      enqueueSnackbar(`Optimize tab: ${fromOptimize.error}`, { variant: 'warning' });
      return;
    }
    // Scope and parameters only: what to run and apply stays this schedule's.
    setForm((f) => ({ ...f, settings: optimizeRequestToSettings(fromOptimize.settings || {}) }));
    enqueueSnackbar("Copied the Optimize tab's scope and parameters. Run and apply choices are unchanged.", { variant: 'success' });
  };

  const settings = form?.settings;
  // Step numbers follow what actually runs.
  const stepNo = form?.preIperf ? { iperf: 1, optimize: 2, apply: 3 } : { iperf: null, optimize: 1, apply: 2 };
  // CAPs optimize covers that the iperf test first does not measure.
  const iperfMissesScope = (() => {
    if (!form?.preIperf || form.iperfTest.allCaps || !settings) return [];
    const measured = new Set(form.iperfTest.capSerials);
    let optimized = [];
    if (settings.scopeMode === 'caps') optimized = settings.capSerials;
    else if (settings.scopeMode === 'site') optimized = caps.map((c) => c.serial_number);
    else if (settings.scopeMode === 'devices') optimized = capsOfSerials(devices, settings.focusSerials);
    return optimized.filter((sn) => !measured.has(sn));
  })();
  const encoded = settings ? settingsToOptimizeRequest(settings) : null;
  const changed = settings ? changedParamCounts(settings) : {};
  const problems = [];
  if (form) {
    problems.push(...scheduleWhenProblems(form));
    if (form.steps.length === 0) problems.push('Select at least one step to run and apply.');
    if (form.preIperf) {
      const iperfProblem = iperfTestFormProblem(form.iperfTest);
      if (iperfProblem) problems.push(`Iperf first: ${iperfProblem}`);
    }
    if (encoded?.error) problems.push(encoded.error);
  }

  // Selected CAPs, trees or devices that no longer exist: saving is allowed
  // (they may come back), but the run would fail or cover less.
  const haveDevices = (devices || []).length > 0;
  const missing = [];
  if (settings?.scopeMode === 'caps' && haveDevices) {
    const gone = settings.capSerials.filter((sn) => !capBySerial.has(sn));
    if (gone.length) missing.push(`CAP${gone.length > 1 ? 's' : ''} ${gone.join(', ')} not found on this site`);
  }
  if (settings?.scopeMode === 'trees' && treesLoaded) {
    const gone = settings.customTreeIds.filter((id) => !customTrees.some((t) => t.id === id));
    if (gone.length) missing.push(`custom tree${gone.length > 1 ? 's' : ''} ${gone.join(', ')} no longer exist${gone.length > 1 ? '' : 's'}`);
  }
  if (settings?.scopeMode === 'devices' && haveDevices) {
    const gone = settings.focusSerials.filter((sn) => !deviceBySerial.has(sn));
    if (gone.length) missing.push(`${gone.length} selected device${gone.length > 1 ? 's' : ''} not found on this site`);
  }
  const isMissingCap = (sn) => haveDevices && !capBySerial.has(sn);
  const isMissingTree = (id) => treesLoaded && !customTrees.some((t) => t.id === id);
  const isMissingDevice = (sn) => haveDevices && !deviceBySerial.has(sn);
  const scopeOptions = settings && (settings.scopeMode === 'devices' || settings.focusSerials.length > 0)
    ? [...SCHEDULE_SCOPE_OPTIONS, { value: 'devices', label: `Selected devices (${settings.focusSerials.length})` }]
    : SCHEDULE_SCOPE_OPTIONS;
  const chipProps = (missingFn, labelFn) => (value, getTagProps) => value.map((v, index) => (
    <Chip
      {...getTagProps({ index })}
      key={v}
      size="small"
      label={labelFn(v)}
      color={missingFn(v) ? 'error' : 'default'}
      variant={missingFn(v) ? 'outlined' : 'filled'}
    />
  ));

  const save = async () => {
    if (!form || problems.length > 0) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        enabled: form.enabled,
        timezone: form.timezone,
        start_local: form.startLocal,
        repeat: form.repeat,
        steps: form.steps,
        persist_tree: form.persistTree,
        tolerant_reparent: form.tolerantReparent,
        optimize_request: encoded.body,
        pre_iperf: form.preIperf,
        // Kept while "iperf first" is off, so turning it back on keeps it.
        iperf_request: iperfTestFormToRequest(form.iperfTest),
      };
      const saved = editing
        ? await api.updateOptimizeSchedule(schedule.id, body)
        : await api.createOptimizeSchedule(body);
      enqueueSnackbar(editing ? 'Schedule updated.' : 'Schedule created.', { variant: 'success' });
      onSaved(saved);
    } catch (err) {
      enqueueSnackbar(err?.data?.error || err?.message || 'Failed to save the schedule', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="md">
      <DialogTitle>{editing ? 'Edit scheduled optimize & apply' : 'New scheduled optimize & apply'}</DialogTitle>
      <DialogContent dividers>
        {!form ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : (
          <Stack spacing={2.5}>
            {tzError && <Alert severity="error">{tzError}</Alert>}

            <ScheduleWhenFields form={form} onChange={set} tzOptions={tzOptions} editing={editing} />

            {/* The run, in the order it executes. */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Each run</Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                {form.preIperf && (
                  <>
                    <Chip size="small" color="info" icon={<SpeedIcon />} label="Iperf test" />
                    <ArrowForwardIcon fontSize="small" color="action" />
                  </>
                )}
                <Chip size="small" color="primary" icon={<AutoFixHighIcon />} label="Optimize" />
                <ArrowForwardIcon fontSize="small" color="action" />
                <Chip size="small" color="success" icon={<PlayArrowIcon />} label="Apply the plan" />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                A run is skipped (and shown as skipped) while another optimize, iperf test or plan is in progress for
                this site. Applied plans appear in the Plans tab. Segmentation never runs.
              </Typography>
            </Box>

            <StepCard
              step={form.preIperf ? stepNo.iperf : null}
              color="info"
              icon={<SpeedIcon fontSize="small" />}
              title="Iperf test first"
              subtitle={form.preIperf
                ? 'Measures the links right before optimizing. Optimize waits until every run of this test has finished (up to 4 hours) and then uses these measurements. If the test is cancelled or does not finish, nothing is optimized or applied.'
                : 'Optional. Off: optimize uses the best recent iperf measurement, if any (when Use iperf is on in its parameters).'}
              active={form.preIperf}
              action={(
                <FormControlLabel
                  sx={{ mr: 0 }}
                  labelPlacement="start"
                  control={<Switch checked={form.preIperf} onChange={(e) => set({ preIperf: e.target.checked })} />}
                  label={<Typography variant="body2">{form.preIperf ? 'On' : 'Off'}</Typography>}
                />
              )}
            >
              {form.preIperf && (
                <>
                  {iperfMissesScope.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Optimize (step {stepNo.optimize}) covers {iperfMissesScope.length === 1 ? 'CAP' : 'CAPs'}{' '}
                      {iperfMissesScope.join(', ')}, which this test does not measure; optimize falls back to estimates there.
                    </Alert>
                  )}
                  <IperfTestTargetFields
                    value={form.iperfTest}
                    onChange={(patch) => setForm((f) => ({ ...f, iperfTest: { ...f.iperfTest, ...patch } }))}
                    devices={devices}
                    capsLabel="CAPs to measure"
                  />
                </>
              )}
            </StepCard>

            <StepCard
              step={stepNo.optimize}
              color="primary"
              icon={<AutoFixHighIcon fontSize="small" />}
              title="Optimize"
              subtitle="What to optimize, where, and with which parameters. Each selected algorithm's changes are applied in the next step."
              active
            >
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="subtitle2">Algorithms</Typography>
                  <FormGroup row>
                    {SCHEDULE_STEP_OPTIONS.map((o) => (
                      <FormControlLabel
                        key={o.value}
                        control={<Checkbox checked={form.steps.includes(o.value)} onChange={() => toggleStep(o.value)} />}
                        label={o.label}
                      />
                    ))}
                  </FormGroup>
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center">
                    <Typography variant="subtitle2" sx={{ flex: 1 }}>Optimize scope</Typography>
                    <Button
                      size="small"
                      startIcon={<MapIcon />}
                      onClick={() => setScopeMapOpen(true)}
                      disabled={(devices || []).length === 0}
                    >
                      Select on map
                    </Button>
                  </Stack>
              <RadioGroup row value={settings.scopeMode} onChange={(e) => setSetting('scopeMode', e.target.value)}>
                {scopeOptions.map((o) => (
                  <FormControlLabel key={o.value} value={o.value} control={<Radio size="small" />} label={o.label} />
                ))}
              </RadioGroup>
              {settings.scopeMode === 'caps' && (
                <Autocomplete
                  multiple
                  size="small"
                  options={caps.map((c) => c.serial_number)}
                  value={settings.capSerials}
                  onChange={(_e, v) => setSetting('capSerials', v)}
                  getOptionLabel={(serial) => (capBySerial.has(serial) ? deviceLabel(capBySerial.get(serial)) : serial)}
                  renderTags={chipProps(isMissingCap, (serial) => (capBySerial.has(serial) ? deviceLabel(capBySerial.get(serial)) : serial))}
                  renderInput={(params) => <TextField {...params} label="CAPs" placeholder={caps.length ? 'Select CAPs' : 'No CAPs loaded'} />}
                  sx={{ mt: 1 }}
                />
              )}
              {settings.scopeMode === 'trees' && (
                <Autocomplete
                  multiple
                  size="small"
                  options={customTrees.map((t) => t.id)}
                  value={settings.customTreeIds}
                  onChange={(_e, v) => setSetting('customTreeIds', v)}
                  getOptionLabel={(id) => customTrees.find((t) => t.id === id)?.name || id}
                  renderTags={chipProps(isMissingTree, (id) => customTrees.find((t) => t.id === id)?.name || id)}
                  renderInput={(params) => <TextField {...params} label="Custom trees" placeholder={customTrees.length ? 'Select trees' : 'No custom trees'} />}
                  sx={{ mt: 1 }}
                />
              )}
              {settings.scopeMode === 'devices' && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Devices picked on the map. Remove devices here, pick again with Select on map, or switch to another scope.
                  </Typography>
                  <Autocomplete
                    multiple
                    size="small"
                    options={settings.focusSerials}
                    value={settings.focusSerials}
                    onChange={(_e, v) => setSetting('focusSerials', v)}
                    getOptionLabel={(serial) => (deviceBySerial.has(serial) ? deviceLabel(deviceBySerial.get(serial)) : serial)}
                    renderTags={chipProps(isMissingDevice, (serial) => (deviceBySerial.has(serial) ? deviceLabel(deviceBySerial.get(serial)) : serial))}
                    renderInput={(params) => <TextField {...params} label="Devices" />}
                    sx={{ mt: 1 }}
                  />
                </>
              )}
              {missing.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {missing.join('; ')}. The run will fail or cover less than intended; update the scope.
                </Alert>
              )}
              <MapSelectDialog
                open={scopeMapOpen}
                title="Select devices to optimize"
                devices={devices}
                initialSerials={settings.scopeMode === 'devices' ? settings.focusSerials : []}
                hint="Optimize plans and applies changes for the selected devices only."
                applyLabel="Optimize these devices"
                onClose={() => setScopeMapOpen(false)}
                onApply={(serials) => {
                  setForm((f) => ({ ...f, settings: { ...f.settings, scopeMode: 'devices', focusSerials: serials } }));
                  setScopeMapOpen(false);
                }}
              />
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ flex: 1 }}>Optimize parameters</Typography>
                    {copyFromOptimize && <Button size="small" onClick={copyOptimizeTab}>Copy from Optimize tab</Button>}
                    <Button size="small" onClick={resetParams}>Reset to defaults</Button>
                  </Stack>
              {SCHEDULE_PARAM_GROUPS.map((group) => (
                <Accordion key={group.id} disableGutters variant="outlined" sx={{ '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{group.label}</Typography>
                    {changed[group.id] > 0 && (
                      <Chip size="small" color="primary" variant="outlined" label={`${changed[group.id]} changed`} sx={{ mr: 1 }} />
                    )}
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1.5}>
                      {group.params.map((p) => (
                        <Grid item xs={12} sm={6} md={p.type === 'bool' ? 6 : 4} key={p.key}>
                          <ParamField param={p} value={settings[p.key]} onChange={(v) => setSetting(p.key, v)} />
                        </Grid>
                      ))}
                      {group.id === 'channels' && (
                        <>
                          <Grid item xs={12} md={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel shrink>Channel groups</InputLabel>
                              <Select
                                multiple
                                displayEmpty
                                notched
                                label="Channel groups"
                                value={settings.channelGroupIds}
                                onChange={(e) => setSetting('channelGroupIds', e.target.value)}
                                renderValue={(ids) => (ids.length === 0
                                  ? `Default (${defaultOptimizeChannelGroupSummary()})`
                                  : ids.map((id) => UNII_CHANNEL_GROUPS.find((g) => g.id === id)?.label || id).join(', '))}
                              >
                                {UNII_CHANNEL_GROUPS.map((g) => (
                                  <MenuItem key={g.id} value={g.id}>{g.label} ({g.band})</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField size="small" fullWidth label="5 GHz channels (optional)" placeholder="e.g. 36, 40, 149" value={settings.channelAllowList5GHz} onChange={(e) => setSetting('channelAllowList5GHz', e.target.value)} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField size="small" fullWidth label="6 GHz channels (optional)" placeholder="e.g. 1, 5, 37" value={settings.channelAllowList6GHz} onChange={(e) => setSetting('channelAllowList6GHz', e.target.value)} />
                          </Grid>
                          <Grid item xs={12}>
                            <FormHelperText>Candidate channels are the selected groups plus any channels listed here. With neither set, the default groups are used.</FormHelperText>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
                </Box>
              </Stack>
            </StepCard>

            <StepCard
              step={stepNo.apply}
              color="success"
              icon={<PlayArrowIcon fontSize="small" />}
              title="Apply the plan"
              subtitle="Applied right after optimize, like Apply entire plan in the Optimize tab, limited to the algorithms above."
              active
            >
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={form.tolerantReparent} onChange={(e) => set({ tolerantReparent: e.target.checked })} />}
                  label="Tolerant reparent (keep scanning for the new parent)"
                />
                <FormControlLabel
                  control={<Checkbox checked={form.persistTree} onChange={(e) => set({ persistTree: e.target.checked })} />}
                  label="Persist tree after apply"
                />
              </FormGroup>
            </StepCard>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {form && problems.length > 0 && (
          <Typography variant="caption" color="error" sx={{ mr: 'auto', pl: 2 }}>{problems[0]}</Typography>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!form || saving || problems.length > 0}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {editing ? 'Save' : 'Create schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
